import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Ticket, UserRound } from 'lucide-react';
import { sdk } from '@/lib/graphql';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { navigationFor } from '@/lib/navigation';
import { useDebounced } from '@/hooks';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { Spinner } from '@/components/ui/misc';
import { StatusBadge } from '@/components/domain/badges';
import { navIcon } from './nav-icon';

/**
 * Paleta de comandos (⌘K).
 *
 * Busca en tres sitios a la vez: destinos de la aplicación (locales, instantes),
 * clientes y tickets (contra la API, con rebote). Los destinos aparecen sin
 * escribir nada, así que la paleta también sirve de navegación por teclado.
 */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim(), 250);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const enabled = debounced.length >= 2;

  const customers = useQuery({
    queryKey: qk.customerSearch(debounced),
    queryFn: () => sdk.CustomerSearch({ query: debounced, limit: 6 }),
    enabled,
  });

  const tickets = useQuery({
    queryKey: qk.tickets({ palette: debounced }),
    queryFn: () =>
      sdk.Tickets({ filter: { search: debounced }, page: { page: 1, pageSize: 6 } }),
    enabled,
  });

  const destinations = navigationFor(user?.role)
    .flatMap((section) => section.items)
    .filter((item) => {
      if (!query.trim()) return true;
      const needle = query.trim().toLowerCase();
      return (
        item.label.toLowerCase().includes(needle) ||
        (item.keywords ?? []).some((keyword) => keyword.includes(needle))
      );
    });

  function go(to: string) {
    onOpenChange(false);
    void navigate(to);
  }

  const loading = enabled && (customers.isFetching || tickets.isFetching);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} label="Buscador global">
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Buscar clientes, tickets o ir a una sección…"
      />
      <CommandList>
        {!loading && destinations.length === 0 && !customers.data?.customerSearch.length && !tickets.data?.tickets.nodes.length ? (
          <CommandEmpty>
            {enabled ? 'Sin resultados.' : 'Escribe al menos dos letras para buscar.'}
          </CommandEmpty>
        ) : null}

        {destinations.length > 0 ? (
          <CommandGroup heading="Ir a">
            {destinations.map((item) => {
              const Icon = navIcon(item.icon);
              return (
                <CommandItem key={item.to} value={item.to} onSelect={() => go(item.to)}>
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner label="Buscando…" />
          </div>
        ) : null}

        {customers.data && customers.data.customerSearch.length > 0 ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Clientes">
              {customers.data.customerSearch.map((customer) => (
                <CommandItem
                  key={customer.id}
                  value={`cliente-${customer.id}`}
                  onSelect={() => go(`/clientes/${customer.id}`)}
                >
                  <UserRound aria-hidden="true" />
                  <span className="truncate">{customer.displayName}</span>
                  <CommandShortcut>{customer.docNumber}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}

        {tickets.data && tickets.data.tickets.nodes.length > 0 ? (
          <>
            <CommandSeparator />
            <CommandGroup heading="Tickets">
              {tickets.data.tickets.nodes.map((ticket) => (
                <CommandItem
                  key={ticket.id}
                  value={`ticket-${ticket.id}`}
                  onSelect={() => go(`/tickets/${ticket.id}`)}
                >
                  <Ticket aria-hidden="true" />
                  <span className="truncate">{ticket.subject}</span>
                  <span className="ml-auto flex shrink-0 items-center gap-2">
                    <StatusBadge status={ticket.status} size="sm" />
                    <span className="font-mono text-2xs text-fg-subtle">{ticket.code}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
